import { useMemo } from "react";
import {
    EntityProfileRevenueBracket,
    EntityProfileType,
} from "@common/types/entity";
import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import { useBillingStatus } from "./useBillingStatus";
import { useEntities } from "./useEntities";
import { useActiveWorkspace } from "./useActiveWorkspace";

const ENTERPRISE_REVENUE_BRACKETS = [EntityProfileRevenueBracket.MORE_THAN_1M];

export function useRecommendedPlan() {
    const entities = useEntities();
    const workspace = useActiveWorkspace();

    const { currentPlan, plans, isFetched } = useBillingStatus();

    const recommendedPlan = useMemo(() => {
        if (!isFetched || !entities.length) {
            return null;
        }

        if (workspace?.isFreshbooksCustomer) {
            return FLAT_RATE_PLANS.FRESHBOOKS;
        }

        if (
            entities.some(
                (entity) =>
                    entity.profile?.annualRevenue &&
                    ENTERPRISE_REVENUE_BRACKETS.includes(
                        entity.profile.annualRevenue,
                    ),
            )
        ) {
            return FLAT_RATE_PLANS.ENTERPRISE;
        }

        const hasCCorpEntity = entities.some(
            (entity) => entity.legalType === EntityProfileType.C_CORP,
        );

        if (hasCCorpEntity) {
            return FLAT_RATE_PLANS.ENTERPRISE;
        }

        const hasSCorpOrPartnershipEntity = entities.some(
            (entity) =>
                entity.legalType === EntityProfileType.S_CORP ||
                entity.legalType === EntityProfileType.PARTNERSHIP,
        );

        if (hasSCorpOrPartnershipEntity) {
            return FLAT_RATE_PLANS.PLUS;
        }

        return FLAT_RATE_PLANS.BASIC;
    }, [isFetched, entities, workspace]);

    const isUpgrade =
        plans.findIndex(({ plan }) => plan === recommendedPlan) >
        plans.findIndex(({ plan }) => plan === currentPlan);

    return isUpgrade ? recommendedPlan : null;
}
